import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  TOURNAMENT_JOIN_CUTOFF_MS,
  parseAvatarConfig,
  type TournamentMatchView,
  type TournamentPublic,
} from '@shared/index'
import { setTournamentDeckLock, useDeckStore } from './deck'
import { ApiError } from '../api/http'
import {
  fetchTournamentCurrent,
  joinTournament,
  leaveTournament,
} from '../api/tournaments'
import { activateFirstPlayable, listDeckPlayOptions } from '../game/localDeck'
import { resolveDuelMatId } from '../game/gamemats'
import { resolveDuelSleeveId } from '../game/sleeves'
import { useAuthStore } from './auth'
import { useDuelPrefsStore } from './duelPrefs'
import { usePlayerStore } from './player'

function readPublic(raw: unknown): TournamentPublic | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as TournamentPublic
  if (row.status !== 'upcoming' && row.status !== 'registration' && row.status !== 'live') {
    return null
  }
  if (typeof row.startsAt !== 'string' || typeof row.opensAt !== 'string') return null
  if (!Array.isArray(row.entrants) || !Array.isArray(row.matches)) return null
  return row
}

export const useTournamentStore = defineStore('tournament', () => {
  const current = ref<TournamentPublic | null>(null)
  const error = ref('')
  const busy = ref(false)
  const pickerOpen = ref(false)
  const lockedDeckId = ref<string | null>(null)
  const autoDeckName = ref('')

  const joined = computed(() => {
    const me = useAuthStore().user?.id
    if (!me || !current.value) return false
    return current.value.entrants.some((entry) => entry.userId === me)
  })

  const myWaitingMatch = computed((): TournamentMatchView | null => {
    const me = useAuthStore().user?.id
    const live = current.value
    if (!me || !live || live.status !== 'live') return null
    return (
      live.matches.find(
        (match) =>
          match.status === 'waiting' &&
          (match.player0?.userId === me || match.player1?.userId === me),
      ) ?? null
    )
  })

  /** Waiting check-in or a live room this account still has to play. */
  const myActiveMatch = computed((): TournamentMatchView | null => {
    const me = useAuthStore().user?.id
    const live = current.value
    if (!me || !live || live.status !== 'live') return null
    return (
      live.matches.find(
        (match) =>
          (match.status === 'waiting' || match.status === 'live') &&
          (match.player0?.userId === me || match.player1?.userId === me),
      ) ?? null
    )
  })

  function apply(raw: unknown): void {
    if (raw == null) {
      current.value = null
      noteLock()
      return
    }
    const next = readPublic(raw)
    if (next) current.value = next
    noteLock()
  }

  function noteLock(): void {
    const me = useAuthStore().user?.id
    const row = current.value
    const holding = Boolean(
      me &&
        row &&
        !row.sample &&
        (row.status === 'registration' || row.status === 'live') &&
        row.entrants.some((entry) => entry.userId === me),
    )
    if (!holding) lockedDeckId.value = null
    setTournamentDeckLock(lockedDeckId.value)
  }

  async function refresh(): Promise<void> {
    const token = useAuthStore().token
    if (!token) {
      current.value = null
      lockedDeckId.value = null
      noteLock()
      return
    }
    try {
      const row = await fetchTournamentCurrent(token)
      current.value = row
      lockedDeckId.value = row.lockedDeckId ?? null
      noteLock()
    } catch {
      /* keep the last snapshot */
    }
  }

  function openPicker(): void {
    error.value = ''
    autoDeckName.value = ''
    const row = current.value
    if (!row || row.sample || row.status !== 'registration' || joined.value) return
    const start = Date.parse(row.startsAt)
    if (!Number.isFinite(start) || start - Date.now() <= TOURNAMENT_JOIN_CUTOFF_MS) {
      error.value = 'closed_soon'
      return
    }
    const playable = listDeckPlayOptions().some((option) => option.playable)
    if (!playable) {
      error.value = 'deck'
      return
    }
    pickerOpen.value = true
  }

  function closePicker(): void {
    pickerOpen.value = false
  }

  async function join(deckId: string, auto = false): Promise<void> {
    const token = useAuthStore().token
    error.value = ''
    if (!auto) autoDeckName.value = ''
    if (!token || !deckId) {
      error.value = 'deck'
      return
    }
    const slot = useDeckStore().decks.find((row) => row.id === deckId)
    const name = (slot?.name ?? '').trim()
    busy.value = true
    try {
      const player = usePlayerStore()
      const prefs = useDuelPrefsStore()
      await joinTournament(token, {
        deckId,
        avatar: parseAvatarConfig(player.avatar),
        matId: resolveDuelMatId(prefs.matId),
        sleeveId: resolveDuelSleeveId(prefs.sleeveId),
      })
      await refresh()
      if (auto) autoDeckName.value = name
    } catch (err) {
      error.value = err instanceof ApiError ? err.code : 'deck'
    } finally {
      busy.value = false
    }
  }

  async function joinActive(): Promise<void> {
    activateFirstPlayable()
    const id = useDeckStore().activeId
    if (!id) {
      error.value = 'deck'
      return
    }
    await join(id, true)
  }

  async function leave(): Promise<void> {
    const token = useAuthStore().token
    if (!token) return
    error.value = ''
    autoDeckName.value = ''
    busy.value = true
    try {
      await leaveTournament(token)
      await refresh()
    } catch (err) {
      error.value = err instanceof ApiError ? err.code : 'not_open'
    } finally {
      busy.value = false
    }
  }

  return {
    current,
    error,
    busy,
    pickerOpen,
    lockedDeckId,
    autoDeckName,
    joined,
    myWaitingMatch,
    myActiveMatch,
    apply,
    refresh,
    openPicker,
    closePicker,
    join,
    joinActive,
    leave,
  }
})
